import {
  signMessage,
  sendTransaction,
  switchChain,
  getTransactionReceipt,
} from "@wagmi/core";
import { Address } from "viem";

import { config } from "../Web3Modal";

export function formatWalletAddress(address: string) {
  if (!address || address.length < 8) {
    throw new Error(
      "Invalid address. Address must be at least 8 characters long."
    );
  }
  return `${address.slice(0, 6)}...${address.slice(-6)}`;
}

async function signEVMTransaction(transaction: any) {
  // change chain if needed
  await switchChain(config, { chainId: transaction.chainId });

  return sendTransaction(config, {
    chainId: transaction.chainId,
    data: transaction.data,
    to: transaction.to,
    value: transaction.value.toString(),
    gas: transaction.gas,
    gasPrice: transaction.gasPrice,
  });
}

export async function signEvmTransactionWithRetry(
  transaction: any,
  retry = 0
): Promise<any> {
  try {
    if (typeof transaction === "string") {
      const cleanTransactionString = transaction.replace(/'/g, '"');
      transaction = JSON.parse(cleanTransactionString);
    }
    return await signEVMTransaction(transaction);
  } catch (e: any) {
    if (
      e?.data?.message ===
        "execution reverted: ERC20: transfer amount exceeds allowance" &&
      retry <= 5
    ) {
      await new Promise((resolve) => setTimeout(resolve, 5000));
      return await signEvmTransactionWithRetry(transaction, retry + 1);
    } else throw e;
  }
}

export async function waitForEVMTransaction(txHash: string, chainId: number) {
  let receipt = null;
  async function checkTransactionReceipt() {
    try {
      return await getTransactionReceipt(config, {
        hash: txHash as Address,
        chainId,
      });
    } catch (e) {
      return null;
    }
  }
  // Check if transaction receipt exists
  while (receipt === null) {
    // Wait for 5 seconds before checking again
    await new Promise((resolve) => setTimeout(resolve, 5000));
    receipt = await checkTransactionReceipt();
  }
  if (receipt.status !== "success") {
    throw new Error("Transaction failed");
  }

  return receipt;
}

export async function signEvmMessage({
  message,
  address,
}: {
  message: string;
  address: Address;
}) {
  const signature = await signMessage(
    { ...config },
    {
      message,
      account: address,
    }
  );
  return signature;
}
